// Shell data (contracts api/notifications.ts · api/search.ts · api/admin-collection.ts status · api/people.ts teams):
// footer status line, bell notifications (+ read / read all), header search for people and records, the team tree of the
// header team filter. Also the bridge from the API's hash routes ('#/connect?tab=mine') to the app's paths.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiFetch } from './client';
import {
  FooterStatusSchema,
  NotificationListSchema,
  OkResponse,
  ReadAllResponse,
  SearchSchema,
  TeamsTreeSchema,
  type NotificationList,
} from './types';

export const SHELL = {
  status: '/api/v1/status',
  notifications: '/api/v1/notifications',
  readAll: '/api/v1/notifications/read-all',
  read: (id: string) => `/api/v1/notifications/${encodeURIComponent(id)}/read`,
  search: '/api/v1/search',
  teams: '/api/v1/teams',
} as const;

export const shellKeys = {
  status: ['shell', 'status'] as const,
  notifications: ['shell', 'notifications'] as const,
  search: (q: string) => ['shell', 'search', q] as const,
  teams: ['shell', 'teams'] as const,
};

/** How many notifications the bell lists (newest first). */
export const NOTIFICATION_LIMIT = 30;

/**
 * Server hash routes ('#/connect?tab=mine', '#/settings?tab=views', '#/logs/1330?tab=qa') → app paths. Old mockup
 * paths (/settings, /admin/system) keep working through the redirects in routes.tsx. Anything else → null.
 */
export function appPath(href: string | null | undefined): string | null {
  if (!href) return null;
  const m = /^#(\/[\w\-/?=&.,~%]*)$/.exec(href.trim());
  return m ? m[1]! : null;
}

/** Footer: collection server connected · last record (server-wide for system.view, otherwise my own). */
export function useFooterStatus(enabled = true) {
  return useQuery({
    queryKey: shellKeys.status,
    enabled,
    queryFn: ({ signal }) => apiFetch(SHELL.status, { schema: FooterStatusSchema, signal }),
    refetchInterval: 60_000,
    retry: false,
  });
}

const EMPTY_LIST: NotificationList = { items: [], unreadCount: 0, nextCursor: null };

/** Bell notifications. Until the server has the endpoint (P2), 404/501 means "no notifications" so the bell still works. */
export function useNotifications(enabled: boolean) {
  return useQuery({
    queryKey: shellKeys.notifications,
    enabled,
    queryFn: async ({ signal }): Promise<NotificationList> => {
      try {
        return await apiFetch(SHELL.notifications, { query: { limit: NOTIFICATION_LIMIT }, schema: NotificationListSchema, signal });
      } catch (e) {
        if (e instanceof ApiError && (e.status === 404 || e.status === 501)) return EMPTY_LIST;
        throw e;
      }
    },
    refetchInterval: 120_000,
    retry: false,
  });
}

function markLocal(old: NotificationList | undefined, ids: readonly string[] | 'all'): NotificationList | undefined {
  if (!old) return old;
  if (ids === 'all') return { ...old, items: old.items.map((n) => ({ ...n, read: true })), unreadCount: 0 };
  const newlyRead = old.items.filter((n) => !n.read && ids.includes(n.id)).length;
  return { ...old, items: old.items.map((n) => (ids.includes(n.id) ? { ...n, read: true } : n)), unreadCount: Math.max(0, old.unreadCount - newlyRead) };
}

/**
 * Read marks (POST …/:id/read · …/read-all). The list is updated at once and refetched afterwards; a failed read mark
 * is only a convenience lost, so it does not raise an error toast.
 */
export function useMarkNotifications() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: readonly string[] | 'all') => {
      if (ids === 'all') await apiFetch(SHELL.readAll, { method: 'POST', schema: ReadAllResponse });
      else await Promise.all(ids.map((id) => apiFetch(SHELL.read(id), { method: 'POST', schema: OkResponse })));
    },
    onMutate: async (ids) => {
      await qc.cancelQueries({ queryKey: shellKeys.notifications });
      qc.setQueryData<NotificationList>(shellKeys.notifications, (old) => markLocal(old, ids));
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: shellKeys.notifications });
    },
  });
}

/** Header search: people (people.view; pseudonyms match only as pseudonyms) and records (others' only by number). */
export function useHeaderSearch(q: string, enabled: boolean) {
  const query = q.trim().slice(0, 60);
  return useQuery({
    queryKey: shellKeys.search(query),
    enabled: enabled && query.length > 0,
    queryFn: ({ signal }) => apiFetch(SHELL.search, { query: { q: query, limit: 6 }, schema: SearchSchema, signal }),
    staleTime: 30_000,
    retry: false,
  });
}

/** Team tree for the header team filter (only fetched where the filter exists). */
export function useTeamsTree(enabled: boolean) {
  return useQuery({
    queryKey: shellKeys.teams,
    enabled,
    queryFn: ({ signal }) => apiFetch(SHELL.teams, { schema: TeamsTreeSchema, signal }),
    staleTime: 5 * 60_000,
    retry: false,
  });
}
